import { useState } from "react";
import { ImageOff } from "lucide-react";

/**
 * SafeImage — thin <img> wrapper with a graceful broken-image fallback.
 *
 * Real signboard photos are served from static files in the repo
 * (`/signboards/<slug>/...`). If a file is ever missing/renamed, a
 * bare <img> shows the browser's default broken-image glyph — jarring
 * and unbranded. This swaps to a quiet "photo unavailable" placeholder
 * instead, using the same `ImageOff` icon already used for the "gallery
 * being assembled" empty state elsewhere on the signboards pages.
 *
 * Never fabricates a replacement photo — it only changes what happens
 * when the real one fails to load.
 */
export default function SafeImage({
  src,
  alt,
  imgClassName,
  fallbackClassName = "w-full h-full flex items-center justify-center bg-[#F1F5F9]",
  fallbackLabel,
  fallbackIconClassName = "h-6 w-6",
  loading = "lazy",
  onErrorExtra,
  ...rest
}) {
  const [broken, setBroken] = useState(false);

  if (broken) {
    return (
      <div className={fallbackClassName} role="img" aria-label={alt}>
        <div className="flex flex-col items-center gap-1.5 text-[#94A3B8]">
          <ImageOff className={fallbackIconClassName} strokeWidth={1.6} aria-hidden />
          {fallbackLabel && (
            <span className="text-[10.5px] tracking-[0.14em] uppercase font-bold">
              {fallbackLabel}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      className={imgClassName}
      onError={() => {
        setBroken(true);
        onErrorExtra?.();
      }}
      {...rest}
    />
  );
}
